import {Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {I18NService} from '../../i18n/i18n.service';
import {MsgBoxService} from '../../shared/msg-box.service';
import {ApiService, OnboardingFile, PasswordStrengthMessage} from '../../api/api.service';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {BehaviorSubject, Subject} from 'rxjs';
import {validateEq, validateFloat, validateInteger, validateNotBlank} from '../../commons/validators';
import {faEye, faEyeSlash} from '@fortawesome/pro-light-svg-icons';
import {
    ONBOARDING_STEPS,
    OnboardingECMemberDocument,
    OnboardingState,
    OnboardingUser
} from 'smag-commons/dto/api/onboarding';
import {SpinnerData} from '../../shared/spinner/spinner.component';
import {DialogService} from '../../shared/dialog/dialog.service';
import {
    EC_DOCUMENT_METADATA_OPTIONAL,
    EC_DOCUMENT_PP,
    ECAcceptedDocument,
    ECDocument,
    ECEntitySubtype,
    ECJuridicalType,
    ECLandRegistryData,
    ECLegalForm,
    ECMemberAccessType,
    ECMemberAddress,
    ECMemberPOD,
    ECPodUtilityType,
    ECSubjectType,
    EnergyCommunityNamedPlant,
    EnergyCommunityNamedPod,
    EntitySubType,
    PodStatisticalProfile,
    SpatialCoordinates,
    UpdateECMemberRequest
} from 'smag-commons/cfg/hierarchy';
import {AddressExt} from '../../shared/google-maps-address-autocomplete/google-maps-address-autocomplete.component';
import {
    ATECO_MAX_CHARS,
    DEFAULT_MAX_CHARS,
    EMAIL_MAX_CHARS,
    IBAN_MAX_CHARS,
    LAND_REGISTRY_MAX_CHARS,
    MOBILE_MAX_CHARS,
    POD_MAX_CHARS,
    PROFILATIONS_MAX_CHARS,
    TAX_ID_MAX_CHARS,
    TOTAL_CONSUMPTION_MAX_CHARS,
    VAT_NUMBER_MAX_CHARS
} from '../../shared/limits';
import {isDefined, mkTextField} from '../../commons/commons';
import {
    ATECO_PATTERN,
    EMAIL_PATTERN,
    FISCAL_CODE_PATTERN,
    IBAN_PATTERN,
    MOBILE_PATTERN,
    PRODUCTION_POD_PATTERN,
    PROVIDER_PATTERN,
    VAT_NUMBER_PATTERN
} from '../../shared/patterns';
import {
    NON_RESIDENTIAL_CONSUMPTION_PROFILE_TEMPLATE_ZONES,
    NonResidentialConsumptionProfileTemplate,
    Phases,
    RateMode,
    ResidentialConsumptionProfileTemplate,
    VoltageLevel
} from 'smag-commons/cfg';
import {MatRadioChange} from '@angular/material/radio';
import {
    ConfigurableField,
    ConsumerPodStatisticalProfileRequest,
    CustomerConfig,
    DocumentConfig,
    FieldConfig,
    FieldConfigScope,
    ProducerPodStatisticalProfileRequest,
    ProsumerPodStatisticalProfileRequest
} from 'smag-commons/dto/api';
import {
    getNonResidentialConsumptionProfileTemplate,
    getResidentialConsumptionProfileTemplate,
    googleCompatibleTextAddress,
    mkMemberAddress
} from 'smag-commons/fcn';
import * as _ from 'lodash';
import {environment} from 'src/environments/environment';
import {PodStatisticalProfileRequestPlantType} from 'smag-commons/dto/api/profiles';
import {COUNTRIES, Country, LOCALIZATIONS as COUNTRY_LOCALIZATIONS} from '../../shared/countries';
import {MUNICIPALITIES, Municipality} from '../../shared/municipalities';
import * as moment from 'moment-timezone';
import {Moment} from 'moment-timezone';
import {DEFAULT_DECIMAL_SEPARATOR, DEFAULT_TIMEZONE} from '../../i18n/i18n-defaults';
import {TimeSlot} from 'smag-commons/src/cfg/costs';

class Strength {

    private clzz: string;
    private wdth: string;
    private qlty: number;
    private vld: boolean;

    constructor(quality?: number) {
        this.quality = quality || 0;
    }

    get quality() {
        return this.qlty;
    }

    set quality(q) {
        const v = Strength.asPercentage(q);
        this.wdth = `${v}%`;
        this.clzz = Strength.clazzForPercentage(v);
        this.qlty = v;
    }

    get validity() {
        return this.vld;
    }

    set validity(v) {
        this.vld = v;
    }

    get clazz() {
        return this.clzz;
    }

    get width() {
        return this.wdth;
    }

    static clazzForPercentage(p: number): string {
        if (p < 40.0) {
            return 'progress-danger';
        }
        if (p < 100.0) {
            return 'progress-warning';
        }
        return 'progress-good';
    }

    static asPercentage(q: number): number {
        return Math.min(Math.max(0, q * 100), 100);
    }
}

interface MimeType {
    name: string;
    type: string;
}

interface TmpDocument {
    fileName: string;
    file: File;
}

const DEFAULT_COUNTRY = 'IT';
const MIN_SIGNATORY_AGE = 18;

/**
 * A single possible choice for an "enum" parameter.
 */
export interface EnumValue {
    value: any;
    name: string;
}

export interface EconomicsConfig {
    residential_consumption_profile_templates: ResidentialConsumptionProfileTemplate[];
    non_residential_consumption_profile_template: NonResidentialConsumptionProfileTemplate[];
}

const DIALOG_WIDTH = 700;
const TOTP_TIMEOUT = 300000;

const PERSONAL_DATA = 'Personal data';
const YOUR_UTILITIES = 'Your utilities';
const ENERGY_PROFILING = 'Energy profiling';
const CREDENTIALS = 'Credentials';
const ACCEPTANCE = 'Consents';

// tslint:disable-next-line:prefer-const
let STEPS: [string, FormGroup][] = [
    [PERSONAL_DATA, null],
    [YOUR_UTILITIES, null],
    [ENERGY_PROFILING, null],
    [CREDENTIALS, null],
    [ACCEPTANCE, null]
];
const MAX_FILE_MB = 2;
const FILE_MIME_TYPES: MimeType[] = [
    {
        type: 'application/pdf',
        name: 'PDF'
    },
    {
        type: 'image/jpeg',
        name: 'JPG'
    },
    {
        type: 'image/png',
        name: 'PNG'
    }
];

@Component({
    selector: 'app-onboarding',
    templateUrl: './onboarding.component.html',
    styleUrls: ['./onboarding.component.scss']
})
export class OnboardingComponent implements OnInit {
    private destroyed: Subject<void> = new Subject<void>();
    private totp: string;
    private messages: PasswordStrengthMessage[] = [];
    private utilitiesForm: FormGroup;
    private energyProfilingForm: FormGroup;
    private savedTimeToSleep: string;
    private tmpSubjectDocuments: { [id: string]: TmpDocument } = {};
    private economicsConfig: EconomicsConfig;
    private customerConfig: CustomerConfig;
    private fieldConfigs: FieldConfig[] = [];
    private podStatisticalProfile: Map<string, PodStatisticalProfile | null> = new Map<string, PodStatisticalProfile>();
    private spatialCoordinates: SpatialCoordinates = null;
    private readonly countriesByCode: { [code: string]: Country } = {};

    @ViewChild('spinner') private spinnerDialog: TemplateRef<any>;

    strength: Strength = new Strength();
    personalDataForm: FormGroup;
    credentialsForm: FormGroup;
    acceptanceForm: FormGroup;
    totpForm: FormGroup;
    onboardingMember: OnboardingState;
    hidePsw = true;
    hideRepPsw = true;
    pwErrorMessages: string[];
    errorCount = 0;
    timerMessage = '';
    timeToSleep: number;
    currentStep = 0;  // 'Personal data';
    currentJuridicalType: ECJuridicalType;
    currentLegalForm: ECLegalForm;
    currentSubjectType: ECSubjectType;
    currentEntitySubtype: ECEntitySubtype;
    subjectDocumentConfig: DocumentConfig[] = [];
    ecDocuments: ECDocument[] = [];
    cPowerValues: EnumValue[];
    podUtilitiesMap: { [key: string]: EnumValue[] } = {};
    showBigPP = false;
    bigPPLink: string = null;
    countries: Country[] = [];
    definedMemberAddress: ECMemberAddress = null;

    readonly isDefined = isDefined;
    readonly ConfigurableField = ConfigurableField;
    readonly ECJuridicalType = ECJuridicalType;
    readonly ACCEPTED_MIME_TYPES = FILE_MIME_TYPES.map(mt => mt.type).join(',');
    readonly STEPS = STEPS;
    readonly faEye = faEye;
    readonly faEyeSlash = faEyeSlash;
    readonly TOTP_TIMEOUT = TOTP_TIMEOUT;
    readonly ONBOARDING_STEPS = ONBOARDING_STEPS;
    readonly EMAIL_MAX_CHARS = EMAIL_MAX_CHARS;
    readonly TAX_ID_MAX_CHARS = TAX_ID_MAX_CHARS;
    readonly IBAN_MAX_CHARS = IBAN_MAX_CHARS;
    readonly VAT_NUMBER_MAX_CHARS = VAT_NUMBER_MAX_CHARS;
    readonly ATECO_MAX_CHARS = ATECO_MAX_CHARS;
    readonly LAND_REGISTRY_MAX_CHARS = LAND_REGISTRY_MAX_CHARS;
    readonly MOBILE_MAX_CHARS = MOBILE_MAX_CHARS;
    readonly PROFILATIONS_MAX_CHARS = PROFILATIONS_MAX_CHARS;
    readonly TOTAL_CONSUMPTION_MAX_CHARS = TOTAL_CONSUMPTION_MAX_CHARS;
    readonly ECPodUtilityType = ECPodUtilityType;
    readonly PERSONAL_DATA = PERSONAL_DATA;
    readonly YOUR_UTILITIES = YOUR_UTILITIES;
    readonly ENERGY_PROFILING = ENERGY_PROFILING;
    readonly CREDENTIALS = CREDENTIALS;
    readonly ACCEPTANCE = ACCEPTANCE;
    readonly POD_MAX_CHARS = POD_MAX_CHARS;
    readonly MAX_FILE_MB = MAX_FILE_MB;
    readonly DEFAULT_MAX_CHARS = DEFAULT_MAX_CHARS;
    readonly podVoltages = [
        {value: VoltageLevel.LOW, name: 'Low__{voltage}'},
        {value: VoltageLevel.MEDIUM, name: 'Medium__{voltage}'}
    ];
    readonly podPhases = [
        {value: Phases.SINGLE, name: 'Single phase'},
        {value: Phases.THREE, name: 'Three phase'}
    ];
    readonly MUNICIPALITIES = MUNICIPALITIES;

    constructor(private readonly i18n: I18NService,
                private readonly msgBoxService: MsgBoxService,
                private readonly route: ActivatedRoute,
                private readonly apiService: ApiService,
                private readonly fb: FormBuilder,
                private readonly router: Router,
                private readonly dialogService: DialogService) {
        this.totpForm = this.fb.group({
            smsTotp: new FormControl(null, [Validators.required, Validators.maxLength(6), Validators.minLength(6)]),
        });
        this.cPowerValues = getContractualPowerValues();
        const localizedCountries = COUNTRY_LOCALIZATIONS[this.i18n.language];
        this.countries = localizedCountries ?
            COUNTRIES.map(country => ({
                code: country.code,
                name: localizedCountries[country.code] || country.name,
            }) as Country) :
            COUNTRIES;
        this.countriesByCode = {};
        this.countries.forEach(country => this.countriesByCode[country.code] = country);
    }

    ngOnInit() {
        this.totp = this.route.snapshot.queryParamMap.get('totp');
        this.onboardingMember = this.route.snapshot.data.member;
        this.currentJuridicalType = this.onboardingMember.member.juridical_type;
        this.currentLegalForm = this.onboardingMember.member.legal_form;
        this.currentSubjectType = this.onboardingMember.member.subject_type;
        this.currentEntitySubtype = this.onboardingMember.member.entity_subtype;
        this.customerConfig = this.route.snapshot.data.customerConfig;
        this.fieldConfigs = (this.customerConfig.configurable_fields || {})[FieldConfigScope.ONBOARDING] || [];
        this.bigPPLink = this.customerConfig.privacy_policy_link;
        const noProfilablePods = !this.profilablePods.length;
        // Steps array management
        if (noProfilablePods && (!this.onboardingMember.member.access_type || (this.onboardingMember.member.access_type === ECMemberAccessType.NONE))) {
            this.STEPS.splice(2, 2);
            // ['Personal data','Your utilities','Acceptance']
        } else if (noProfilablePods && (!!this.onboardingMember.member.access_type && this.onboardingMember.member.access_type !== ECMemberAccessType.NONE)) {
            this.STEPS.splice(2, 1);
            // ['Personal data','Your utilities','Credentials'?,'Acceptance']
        } else if (!noProfilablePods && (!this.onboardingMember.member.access_type || (this.onboardingMember.member.access_type === ECMemberAccessType.NONE))) {
            this.STEPS.splice(3, 1);
            // ['Personal data','Your utilities','Energy profiling'?,'Acceptance']
        }
        // Iterate through the steps, then create their respective forms only if those steps exist
        for (const step of STEPS) {
            switch (step[0]) {
                case PERSONAL_DATA:
                    this.personalDataForm = this.fb.group({});
                    step[1] = this.personalDataForm;
                    break;
                case YOUR_UTILITIES:
                    this.utilitiesForm = this.fb.group({
                        pods: this.fb.array([])
                    });
                    step[1] = this.utilitiesForm;
                    break;
                case ENERGY_PROFILING:
                    this.energyProfilingForm = this.fb.group({
                        podProfiles: this.fb.array([])
                    });
                    step[1] = this.energyProfilingForm;
                    break;
                case CREDENTIALS:
                    this.credentialsForm = this.fb.group({
                            password: new FormControl(null, [Validators.required]),
                            repeatedPassword: new FormControl(null, [Validators.required])
                        },
                        {
                            validators: [samePwdValidator(), this.pwValidator()]
                        });
                    step[1] = this.credentialsForm;
                    break;
                case ACCEPTANCE:
                    this.acceptanceForm = this.fb.group({});
                    step[1] = this.acceptanceForm;
                    break;
            }
        }

        if (this.isCompany && this.isEditable(ConfigurableField.VAT_CODE, this.onboardingMember.member.personal_data.piva)) {
            this.personalDataForm.addControl('vatNumber',
                new FormControl(this.onboardingMember.member.personal_data.piva || null,
                    [this.validateIfMandatory(ConfigurableField.VAT_CODE, validateNotBlank), Validators.pattern(VAT_NUMBER_PATTERN)]));
        }
        if (this.isEditable(ConfigurableField.TAX_CODE, this.onboardingMember.member.personal_data.cf)) {
            this.personalDataForm.addControl('taxCode',
                new FormControl(this.onboardingMember.member.personal_data.cf || null,
                [this.validateIfMandatory(ConfigurableField.TAX_CODE, validateNotBlank), Validators.pattern(this.currentJuridicalType === ECJuridicalType.INDIVIDUAL ? FISCAL_CODE_PATTERN : VAT_NUMBER_PATTERN)]));
        }
        if (!this.onboardingMember.member.personal_data.address) {
            this.personalDataForm.addControl('address', new FormControl(null, [Validators.required]));
        }
        if (this.isEditable(ConfigurableField.PEC, this.onboardingMember.member.personal_data.pec)) {
            this.personalDataForm.addControl('pec',
                new FormControl(this.onboardingMember.member.personal_data.pec || null,
                    [this.validateIfMandatory(ConfigurableField.PEC, validateNotBlank), Validators.pattern(EMAIL_PATTERN)]));
        }
        if (this.isEditable(ConfigurableField.IBAN, this.onboardingMember.member.personal_data.iban)) {
            this.personalDataForm.addControl('iban',
                new FormControl(this.onboardingMember.member.personal_data.iban || null,
                    [this.validateIfMandatory(ConfigurableField.IBAN, validateNotBlank), Validators.pattern(IBAN_PATTERN)]));
        }
        if (this.isEditable(ConfigurableField.PHONE_NUMBER, this.onboardingMember.member.personal_data.telephone_number)) {
            this.personalDataForm.addControl('telephone_number',
                new FormControl(this.onboardingMember.member.personal_data.telephone_number || null,
                    [this.validateIfMandatory(ConfigurableField.PHONE_NUMBER, validateNotBlank), Validators.pattern(MOBILE_PATTERN)]));
        }
        if (this.isCompany && this.isEditable(ConfigurableField.ATECO_CODE, this.onboardingMember.member.personal_data.ateco_code)) {
            this.personalDataForm.addControl('atecoCode',
                new FormControl(this.onboardingMember.member.personal_data.ateco_code || null,
                    [this.validateIfMandatory(ConfigurableField.ATECO_CODE, validateNotBlank), Validators.pattern(ATECO_PATTERN)]));
        }
        if (this.currentJuridicalType === ECJuridicalType.CORPORATE_ENTITY && this.isEditable(ConfigurableField.SIGNATORY_TAX_CODE, this.onboardingMember.member.signatory_tax_code)) {
            this.personalDataForm.addControl('signatoryTaxCode',
                new FormControl(this.onboardingMember.member.signatory_tax_code || null,
                    [this.validateIfMandatory(ConfigurableField.SIGNATORY_TAX_CODE, validateNotBlank), Validators.pattern(FISCAL_CODE_PATTERN)]));
        }
        if (this.isEditable(ConfigurableField.BIRTH_DATE, this.onboardingMember.member.personal_data.birth_date)) {
            const birstDateMillis = this.onboardingMember.member.personal_data.birth_date;
            this.personalDataForm.addControl('birthDate',
                new FormControl(isDefined(birstDateMillis) ? moment(birstDateMillis).tz(DEFAULT_TIMEZONE) : null,
                    [this.validateIfMandatory(ConfigurableField.BIRTH_DATE, Validators.required)]));
        }
        if (this.isEditable(ConfigurableField.BIRTH_COUNTRY, this.onboardingMember.member.personal_data.birth_country)) {
            const country = this.onboardingMember.member.personal_data.birth_country;
            this.personalDataForm.addControl('birthCountry',
                new FormControl(this.countriesByCode[country || DEFAULT_COUNTRY] || null,
                    [this.validateIfMandatory(ConfigurableField.BIRTH_COUNTRY, Validators.required)]));
        }
        if (this.isEditable(ConfigurableField.BIRTH_MUNICIPALITY, this.onboardingMember.member.personal_data.birth_municipality)) {
            const municipality = this.onboardingMember.member.personal_data.birth_municipality;
            this.personalDataForm.addControl('birthMunicipality',
                new FormControl(municipality ? MUNICIPALITIES.find(m => m.name === municipality) || null : null,
                    [this.mkBirthMunicipalityValidator()]));
        }
        this.measure(''); // empty psw call to show psw criteria messages before any input
        if (this.credentialsForm) {
            this.credentialsForm.controls.password.valueChanges.pipe(
                debounceTime(250),
                takeUntil(this.destroyed)
            ).subscribe(value => {
                this.measure(value);
            });
        }
        // resume countdown if page is refreshed
        if (localStorage.getItem('savedTimeToSleep') !== null) {
            this.countdown();
        }
        this.fillPodsForm();
        this.apiService.getEconomicsConfig(this.totp).then((resp) => {
            this.economicsConfig = resp;
        }).catch(err => console.error(err));

        this.apiService.getECDocuments(this.totp)
            .subscribe(ecDocuments => {
                this.subjectDocumentConfig = this.customerConfig.subject_documents || [];
                this.ecDocuments = (ecDocuments || []);
                const acceptedDocumentNames = (this.onboardingMember.member?.accepted_documents ?? [])
                    .filter(doc => doc.accepted)
                    .map(doc => doc.name);
                this.ecDocuments.forEach(dta => {
                    const alreadyAccepted = acceptedDocumentNames.includes(dta.document_type);
                    const optional = !alreadyAccepted && this.isOptional(dta);
                    this.acceptanceForm.addControl(`acceptance_${dta.document_type}`,
                        new FormControl({
                            value: alreadyAccepted,
                            disabled: alreadyAccepted
                        }, optional ? [] : Validators.requiredTrue));
                });
                // the default pp document will be used if no pp document is included in ec documents
                this.showBigPP = this.ecDocuments.every(doc => doc.document_type !== EC_DOCUMENT_PP);
                // set form control of pp
                if (this.showBigPP) {
                    const alreadyAccepted = acceptedDocumentNames.includes(EC_DOCUMENT_PP);
                    this.acceptanceForm.addControl('bigPP',
                        new FormControl({value: alreadyAccepted, disabled: alreadyAccepted}, Validators.requiredTrue));
                }
            });
    }

    isOptional(doc: ECDocument) {
        return doc.metadata?.[EC_DOCUMENT_METADATA_OPTIONAL] ?? false;
    }

    isEnabled(field: ConfigurableField): boolean {
        const fieldCfg = this.fieldConfigs.find(cfg => cfg.field === field);
        return !!fieldCfg?.enabled;
    }

    isEditable(field: ConfigurableField, currentValue: any): boolean {
        // NOTE: an editable field must be enabled as well
        const fieldCfg = this.fieldConfigs.find(cfg => cfg.field === field);
        return !!fieldCfg && fieldCfg.enabled && (!isFieldValueDefined(currentValue) || !!fieldCfg.overridable);
    }

    mkFieldName(field: ConfigurableField, name: string, separator = ':'): string {
        const fieldCfg = this.fieldConfigs.find(cfg => cfg.field === field);
        return this.i18n.translate(name) + separator + (fieldCfg?.mandatory ? '*' : '');
    }

    get taxIdMaxChars(): number {
        return this.currentJuridicalType === ECJuridicalType.INDIVIDUAL ? TAX_ID_MAX_CHARS : VAT_NUMBER_MAX_CHARS;
    }

    get pwdContainsForbiddenWords(): boolean {
        return this.credentialsForm.controls.password.dirty && this.credentialsForm.controls.password.hasError('forbidden_words');
    }

    get pwdNotEqual(): boolean {
        return this.credentialsForm.controls.repeatedPassword.dirty && this.credentialsForm.hasError('not_eq');
    }

    get podCtrl(): FormArray {
        return this.utilitiesForm.controls.pods as FormArray;
    }

    get podControls(): FormGroup[] {
        return (this.podCtrl).controls as FormGroup[];
    }

    get podProfileCtrl(): FormArray {
        return this.energyProfilingForm.controls.podProfiles as FormArray;
    }

    get podProfileControls(): FormGroup[] {
        return (this.podProfileCtrl).controls as FormGroup[];
    }

    get profilablePods() {
        return this.onboardingMember.member.pods.filter(pod => this.isPodProfilable(pod));
    }

    get memberMobileNumber() {
        const mobile = this.onboardingMember.member.personal_data.mobile_number || null;
        return mobile ? mobile.substring(0, 3) + '*'.repeat(mobile.length - 5) + mobile.substring(mobile.length - 2, mobile.length) : null;
    }

    get addressFormControl(): FormControl {
        return this.personalDataForm.controls.address as FormControl;
    }

    get mimeTypesNames(): string {
        return FILE_MIME_TYPES.map(mt => mt.name).join(', ');
    }

    get isNextEnabled() {
        if (STEPS[this.currentStep][0] === PERSONAL_DATA) {
            return !STEPS[this.currentStep][1].invalid && !STEPS[this.currentStep][1].pending && this.validateDocuments();
        }
        return !STEPS[this.currentStep][1].invalid && !STEPS[this.currentStep][1].pending;
    }

    get missingDocs() {
        return this.subjectDocumentConfig
            .filter(doc => !this.onboardingMember.member.documents
                .some(d => d.document_type === doc.name));
    }

    get isCompany() {
        return !(this.currentJuridicalType === ECJuridicalType.INDIVIDUAL && this.currentLegalForm === ECLegalForm.OTHER_INDIVIDUALS);
    }

    get forbiddenWords(): string[] {
        return environment.forbiddenWords;
    }

    get maxBirthDate(): Moment {
        return moment().startOf('day').subtract(MIN_SIGNATORY_AGE, 'years');
    }

    get bornInItaly(): boolean {
        // look for an already set country; if none (or editable), fall back on currently edited value
        let birthCountryCode: string = null;
        if (!this.isEditable(ConfigurableField.BIRTH_COUNTRY, this.onboardingMember.member.personal_data.birth_country)) {
            birthCountryCode = this.onboardingMember.member.personal_data.birth_country;
        }  else {
            const editedBirthCountry: Country = this.personalDataForm.controls.birthCountry?.value;
            if (editedBirthCountry) {
                birthCountryCode = editedBirthCountry.code;
            }
        }
        return birthCountryCode === 'IT';
    }

    get birthCountryLabel(): string {
        return this.currentJuridicalType === ECJuridicalType.INDIVIDUAL ? 'Birth country' : 'Signatory\'s birth country';
    }

    get birthMunicipalityLabel(): string {
        return this.currentJuridicalType === ECJuridicalType.INDIVIDUAL ? 'Birth municipality' : 'Signatory\'s birth municipality';
    }

    getVoltageName(voltage: VoltageLevel): string {
        return this.podVoltages.find(v => v.value === voltage)?.name || '';
    }

    getPhasesName(phases: Phases): string {
        return this.podPhases.find(p => p.value === phases)?.name || '';
    }

    podIsReligiousInstitution(pod: ECMemberPOD) {
        return pod.pod_utility_type === ECPodUtilityType.RELIGIOUS_INSTITUTION;
    }

    podIsThirdSector(pod: ECMemberPOD) {
        return pod.pod_utility_type === ECPodUtilityType.THIRD_SECTOR_ORGANIZATION_OR_ENVIRONMENT_PROTECTION_ORGANIZATION;
    }

    podIsOther(pod: ECMemberPOD) {
        return pod.pod_utility_type === ECPodUtilityType.OTHER;
    }

    isFirstRadioSelected(formGroup: FormGroup) {
        return formGroup.controls.podProfileRadio.value === 0;
    }

    togglePsw() {
        this.hidePsw = !this.hidePsw;
    }

    toggleRepPsw() {
        this.hideRepPsw = !this.hideRepPsw;
    }

    getPodProfileFormByPod(podCode: string): FormGroup {
        return ((this.podProfileCtrl).controls as FormGroup[]).find((fg) => fg.controls.podCode.value === podCode);
    }

    getCountryNameByCode(code: string): string {
        const country = this.countriesByCode[code];
        return country?.name || code;
    }

    getCountryName(country: Country): string {
        return country?.name;
    }

    getMunicipalityName(municipality: Municipality): string {
        return municipality?.name;
    }

    onRequestTotp(startCount?: boolean) {
        if (startCount) {
            this.countdown();
        }
        const spinner = this.openSpinner('Sending code...');
        this.apiService.sendSmsTotp(this.totp)
            .then(() => {
                spinner.context.next({
                    loading: false,
                    loadingMessage: this.i18n.translate('Code sent!')
                });
                this.onboardingMember.sms_totp_generated = true;
            })
            .catch(() => {
                spinner.context.next({
                    loading: false,
                    loadingMessage: this.i18n.translate('An error occurred. Try again'),
                    abort: true
                });
                this.msgBoxService.error(this.i18n.translate('An error occurred. Try again'));
            });
    }

    onValidateTotp() {
        this.apiService.verifySmsTotp(this.totp, {totp: this.totpForm.controls.smsTotp.value})
            .then(() => this.onboardingMember.step = ONBOARDING_STEPS.USER_REGISTRATION)
            .catch(() => {
                this.totpForm.controls.smsTotp.reset();
                return this.errorCount++;
            });
    }

    onSubmit() {
        const spinner = this.openSpinner('Registration in progress');
        const password = this.credentialsForm?.controls.password.value || null;
        const currMember = _.cloneDeep(this.onboardingMember.member);
        const [onBoardingUser, files] = this.mkOnboardingMemberUser(password, currMember);
        this.apiService.onboardUser(this.totp, onBoardingUser, files)
            .then(() => {
                spinner.context.next({
                    loading: false,
                    loadingMessage: this.i18n.translate('Registration completed!')
                });
                this.router.navigate(['/c', 'onboarding-completed'],
                    {
                        queryParams: {
                            totp: this.totp,
                            communityName: this.onboardingMember.community_name,
                            webAppUrl: this.customerConfig.mobile_app_url_web,
                            androidAppUrl: this.customerConfig.mobile_app_url_android,
                            iosAppUrl: this.customerConfig.mobile_app_url_ios,
                            ctrlUrl: this.onboardingMember.member.access_type === ECMemberAccessType.ADVANCED ? this.customerConfig.control_panel_url : null,
                            accessType: this.onboardingMember.member.access_type,
                            participationType: this.onboardingMember.member.participation_type
                        },
                        queryParamsHandling: 'merge'
                    }).then();
            })
            .catch(() => {
                spinner.context.next({
                    loading: false,
                    loadingMessage: this.i18n.translate('An error occurred. Try again'),
                    abort: true
                });
                this.msgBoxService.error(this.i18n.translate('An error occurred. Try again'));
            });
    }

    onChangeBirthCountry() {
        const birthMunicipalityCtrl = this.personalDataForm.controls.birthMunicipality;
        if (birthMunicipalityCtrl) {
            birthMunicipalityCtrl.setValue(null);
            birthMunicipalityCtrl.markAsPristine();
        }
    }

    isControlOK(form: FormGroup, controlName: string) {
        return form?.controls[controlName].dirty && !form?.controls[controlName].valid;
    }

    addressChange($event: AddressExt | null) {
        const address = mkMemberAddress($event);
        if (address) {
            this.spatialCoordinates = $event.coordinates;
            this.definedMemberAddress = address;
            this.addressFormControl.setValue(googleCompatibleTextAddress(address));
        }
    }

    uploadedFileName(docId: string): string {
        return this.tmpSubjectDocuments[docId]?.fileName;
    }

    onUploadDocumentFile(docId: string, event: Event) {
        const files = 'files';
        if (event.target[files] && event.target[files].length) {
            const file: File = event.target[files][0];
            if (this.validateFile(file)) {
                this.tmpSubjectDocuments[docId] = {
                    fileName: file.name,
                    file
                };
            }
        } else {
            this.tmpSubjectDocuments[docId] = {
                fileName: null,
                file: null
            };
        }
    }

    onClickRemoveDocument(doc: string) {
        delete this.tmpSubjectDocuments[doc];
    }

    openDocument(documentId: string) {
        this.apiService.getECDocument(this.totp, documentId)
            .then(blob => window.open(URL.createObjectURL(blob), '_blank'))
            .catch(err => console.log((err)));
    }

    openDocLink(link: string) {
        window.open(link, '_blank');
    }

    nextStep(el: HTMLElement) {
        if (STEPS[this.currentStep][0] === YOUR_UTILITIES) {
            this.profilablePods.forEach((pod) => {
                if (pod.pod_utility_type !== ECPodUtilityType.RESIDENTIAL) {
                    this.podUtilitiesMap[pod.pod] = this.economicsConfig.non_residential_consumption_profile_template
                        .filter(cp => cp.type === pod.pod_utility_type && cp.zone === NON_RESIDENTIAL_CONSUMPTION_PROFILE_TEMPLATE_ZONES[this.onboardingMember.community_zone])
                        .map((cp) => {
                            const lb = cp.lb ? cp.lb : 0;
                            const ub = cp.ub ? cp.ub : 0;
                            return {
                                name: `${lb} - ${ub} ${cp.uom}`,
                                value: Math.round((lb + ub) / 2)
                            };
                        });
                }
            });
        }
        if (STEPS[this.currentStep][0] === ENERGY_PROFILING) {
            try {
                this.profilablePods.forEach(pod => {
                    // start by clearing the statistical profile, so that if anything goes wrong with the following REST calls,
                    // the original profile (if any at all) will be kept upon onboarding
                    this.podStatisticalProfile.set(pod.pod, null);
                    if (this.isProducer(pod.resource)) {
                        const reqProducer: ProducerPodStatisticalProfileRequest = {
                            plants: pod.resource.plants.map(plant =>
                                ({
                                    type: this.getPodStatisticalProfileRequestPlantType(plant),
                                    profile: plant.plant.statistical_profile
                                })),
                            rate_mode: RateMode.SINGLE
                        };
                        this.apiService.getProducerPodStatisticalProfile(reqProducer, this.totp)
                            .then((response) => {
                                this.podStatisticalProfile.set(pod.pod, response);
                            })
                            .catch(err => console.error(err));
                    } else {
                        const podForm = this.getPodProfileFormByPod(pod.pod);
                        let profileTemplate: null | ResidentialConsumptionProfileTemplate | NonResidentialConsumptionProfileTemplate = null;
                        if (this.isFirstRadioSelected(podForm)) {
                            profileTemplate = this.generateProfileTemplate(pod, podForm);
                        }
                        const yc = this.isFirstRadioSelected(podForm) ? profileTemplate?.yearly_consumption : this.sanityReplaceFloat(podForm.controls.podYearlyConsumption.value);
                        // if no yearly consumption could be determined (e.g. the user has not supplied any information),
                        // no further computation is performed, so that the original profile (if any at all)
                        // will be kept upon onboarding
                        if (isDefined(yc)) {
                            const req: ConsumerPodStatisticalProfileRequest = {
                                rate_mode: RateMode.SINGLE,
                                type: pod.pod_utility_type,
                                yearly_consumption: yc
                            };
                            this.apiService.getConsumerPodStatisticalProfile(req, this.totp)
                                .then((resp) => {
                                    if (this.isConsumer(pod.resource)) {
                                        this.podStatisticalProfile.set(pod.pod, resp);
                                    } else {
                                        if (this.isProsumer(pod.resource)) {
                                            const reqProsumer: ProsumerPodStatisticalProfileRequest = {
                                                consumed_monthly_energies: resp.absorbed_monthly_energies,
                                                plants: pod.resource.plants.map(plant => ({
                                                    type: this.getPodStatisticalProfileRequestPlantType(plant),
                                                    profile: plant.plant.statistical_profile
                                                })),
                                                rate_mode: resp.rate_mode,
                                                user_type: resp.user_type
                                            };
                                            this.apiService.getProsumerPodStatisticalProfile(reqProsumer, this.totp)
                                                .then((response) => {
                                                    this.podStatisticalProfile.set(pod.pod, response);
                                                })
                                                .catch(err => console.error(err));
                                        }
                                    }
                                })
                                .catch(err => console.error(err));
                        }
                    }
                });
                this.currentStep++;
                el.scrollIntoView();
            } catch (err) {
                console.error(err);
            }
        } else {
            this.currentStep++;
            el.scrollIntoView();
        }
        if (STEPS[this.currentStep][0] === ACCEPTANCE) {
            // TODO get docs
        }
    }

    prevStep(el: HTMLElement) {
        this.currentStep--;
        el.scrollIntoView();
    }

    isConsumer(ecp: EnergyCommunityNamedPod): boolean {
        return !ecp.pod.producer
            && Object.keys(ecp.plants || {}).length === 0;
    }

    isProducer(ecp: EnergyCommunityNamedPod): boolean {
        return ecp.pod.producer as boolean
            && Object.keys(ecp.plants || {}).length > 0;
    }

    isProsumer(ecp: EnergyCommunityNamedPod): boolean {
        return !ecp.pod.producer &&
            Object.keys(ecp.plants || {}).length > 0;
    }

    getEnergyProfile(pod: ECMemberPOD) {
        const ecNamedPod = pod.resource;
        if (ecNamedPod) {
            if (this.isConsumer(ecNamedPod)) {
                return 'Consumer';
            }
            if (this.isProducer(ecNamedPod)) {
                return 'Producer';
            }
            if (this.isProsumer(ecNamedPod)) {
                return 'Prosumer';
            }
        }
        return null;
    }

    detectChanges($event: MatRadioChange, form: FormGroup, podUtilityType: ECPodUtilityType) {
        if ($event.value !== undefined) {
            form.controls.podProfileRadio.setValue($event.value);
            const profileRequiredValidator = this.validateIfMandatory(ConfigurableField.ENERGY_PROFILE, Validators.required);
            const floatValidator = (ctrl: AbstractControl) => validateFloat(ctrl, this.i18n.language);
            if ($event.value === 0) {
                if (!this.isCompany) {
                    form.controls.podPeople.setValidators([profileRequiredValidator, validateInteger]);
                    form.controls.podTv.setValidators([profileRequiredValidator, validateInteger]);
                    form.controls.podPc.setValidators([profileRequiredValidator, validateInteger]);
                    form.controls.podAc.setValidators([profileRequiredValidator, validateInteger]);
                    form.controls.podDishwasher.setValidators([profileRequiredValidator]);
                    form.controls.podHeater.setValidators([profileRequiredValidator]);
                } else {
                    if ((podUtilityType !== ECPodUtilityType.RELIGIOUS_INSTITUTION) &&
                        (podUtilityType !== ECPodUtilityType.THIRD_SECTOR_ORGANIZATION_OR_ENVIRONMENT_PROTECTION_ORGANIZATION)) {
                        form.controls.customRange.setValidators([profileRequiredValidator]);
                    } else {
                        form.controls.customRange.setValidators([]);
                    }
                }
                form.controls.podYearlyConsumption.clearValidators();
            } else {
                if (!this.isCompany) {
                    form.controls.podPeople.clearValidators();
                    form.controls.podTv.clearValidators();
                    form.controls.podPc.clearValidators();
                    form.controls.podAc.clearValidators();
                    form.controls.podDishwasher.clearValidators();
                    form.controls.podHeater.clearValidators();
                } else {
                    form.controls.customRange.clearValidators();
                }
                form.controls.podYearlyConsumption.setValidators([profileRequiredValidator, floatValidator]);
            }
            // tslint:disable-next-line:forin
            for (const ctrl in form.controls) {
                form.controls[ctrl].updateValueAndValidity();
            }
        }
    }

    detectFormChanges(podForm: FormGroup) {
        const currentPod = this.profilablePods.find((ecPod) => ecPod.pod === podForm.controls.podCode.value);
        if (currentPod && this.isFirstRadioSelected(podForm)) {
            const profileTemplate = this.generateProfileTemplate(currentPod, podForm);
            const yc = profileTemplate?.yearly_consumption ?? null;
            podForm.controls.podEstimatedYearlyConsumption.setValue(yc);
        }
    }

    getPodProfileRadio(formGroup: FormGroup) {
        return formGroup.controls.podProfileRadio.value;
    }

    /**
     * @param address to be converted to string
     */
    addressToString(address: ECMemberAddress): string {
        return googleCompatibleTextAddress(address);
    }

    mkOnboardingMemberUser(password: string,
                           member: UpdateECMemberRequest): [OnboardingUser, { [partId: string]: OnboardingFile }] {
        member.signatory_tax_code = this.personalDataForm.controls.signatoryTaxCode ? mkTextField(this.personalDataForm.controls.signatoryTaxCode.value) : member.signatory_tax_code || null;
        // Step 1 personal data
        member.personal_data.piva = this.personalDataForm.controls.vatNumber ? mkTextField(this.personalDataForm.controls.vatNumber.value) : member.personal_data.piva || null;
        member.personal_data.cf = this.personalDataForm.controls.taxCode ? mkTextField(this.personalDataForm.controls.taxCode.value) : member.personal_data.cf || null;
        member.personal_data.address = this.personalDataForm.controls.address ? this.definedMemberAddress : member.personal_data.address || null;
        member.personal_data.pec = this.personalDataForm.controls.pec ? mkTextField(this.personalDataForm.controls.pec.value) : member.personal_data.pec || null;
        member.personal_data.iban = this.personalDataForm.controls.iban ? mkTextField(this.personalDataForm.controls.iban.value) : member.personal_data.iban || null;
        member.personal_data.telephone_number = this.personalDataForm.controls.telephone_number ? mkTextField(this.personalDataForm.controls.telephone_number.value) : member.personal_data.telephone_number || null;
        member.personal_data.ateco_code = this.personalDataForm.controls.atecoCode ? mkTextField(this.personalDataForm.controls.atecoCode.value) : member.personal_data.ateco_code || null;
        member.personal_data.birth_date = this.personalDataForm.controls.birthDate ? this.personalDataForm.controls.birthDate.value?.valueOf() ?? null : member.personal_data.birth_date ?? null;
        member.personal_data.birth_country = this.personalDataForm.controls.birthCountry ? this.personalDataForm.controls.birthCountry.value?.code || null : member.personal_data.birth_country || null;
        member.personal_data.birth_municipality = this.personalDataForm.controls.birthMunicipality ? this.personalDataForm.controls.birthMunicipality.value?.name || null : member.personal_data.birth_municipality || null;
        member.pods.forEach((ecPod) => {
            // Step 2
            const formPod = this.podControls.find((form) => form.controls.podCode.value === ecPod.pod);
            if (formPod) {
                ecPod.has_wifi = formPod.controls.hasWifi.value;
                ecPod.resource.pod.voltage = formPod.controls.podVoltage.value;
                ecPod.resource.pod.phases = formPod.controls.podPhase.value;
                ecPod.resource.pod.contractual_power = formPod.controls.contractualPower ? formPod.controls.contractualPower.value ?? null : ecPod.resource.pod.contractual_power ?? null;
                ecPod.land_registry_data = mkLandRegistryData(ecPod, formPod);
                ecPod.energy_provider = formPod.controls.podProvider ? mkTextField(formPod.controls.podProvider.value) : ecPod.energy_provider || null;
                ecPod.resource.plants.forEach(plant => {
                    const plantProdPodCtrl = formPod.controls[`pod${plant.id}Production`];
                    plant.plant.production_pod = plantProdPodCtrl ? mkTextField(plantProdPodCtrl.value) : plant.plant.production_pod || null;
                });
            }
            // Step 3
            ecPod.resource.pod.statistical_profile = this.podStatisticalProfile.get(ecPod.pod) || ecPod.resource.pod.statistical_profile || null;
        });
        member.accepted_documents = this.mkAcceptedDocuments();
        member.spatial_coordinates = !member.spatial_coordinates ? this.spatialCoordinates : member.spatial_coordinates;

        const files = {};
        Object.entries(this.tmpSubjectDocuments)
            .forEach(([key, {fileName, file}]) => {
                files[key] = {filename: fileName, content: file};
            });

        const documents: OnboardingECMemberDocument[] = [
            ...Object.keys(files).map(fileKey => ({
                part_id: fileKey,
                type: fileKey
            })),
            ...this.onboardingMember.member.documents.map(doc => ({
                part_id: doc.document_type,
                document_id: doc.id,
                type: doc.document_type
            }))
        ];
        return [
            {
                password,
                member_update: {member, documents}
            },
            files
        ];
    }

    getPlantType(plant: EnergyCommunityNamedPlant): string {
        switch (plant.plant.sub_type) {
            case EntitySubType.WIND_PLANT:
                return 'Wind';
            case EntitySubType.PV_PLANT:
                return 'Photovoltaic';
            default:
                return 'Plant';
        }
    }

    private mkAcceptedDocuments(): ECAcceptedDocument[] {
        const ecDocuments = this.ecDocuments.map(dta => ({
            name: dta.document_type,
            timestamp: null, // this will be filled in be
            accepted: this.acceptanceForm.controls[`acceptance_${dta.document_type}`].value
        }));
        return this.showBigPP ? [{
            name: EC_DOCUMENT_PP,
            timestamp: null,
            accepted: this.acceptanceForm.controls.bigPP.value
        }, ...ecDocuments] : ecDocuments;
    }

    private generateProfileTemplate(pod: ECMemberPOD, podForm: FormGroup) {
        let profileTemplate: ResidentialConsumptionProfileTemplate | NonResidentialConsumptionProfileTemplate | null = null;
        if (pod.pod_utility_type === ECPodUtilityType.RESIDENTIAL &&
            (isDefined(podForm.controls.podPeople.value) ||
                isDefined(podForm.controls.podTv.value) ||
                isDefined(podForm.controls.podPc.value) ||
                isDefined(podForm.controls.podAc.value))) {
            profileTemplate = getResidentialConsumptionProfileTemplate(
                this.economicsConfig.residential_consumption_profile_templates,
                +podForm.controls.podPeople.value,
                +podForm.controls.podTv.value,
                +podForm.controls.podPc.value,
                0,
                0,
                podForm.controls.podDishwasher.value ? 1 : 0,
                +podForm.controls.podAc.value,
                podForm.controls.podHeater.value ? 1 : 0,
            );
        } else if (pod.pod_utility_type !== ECPodUtilityType.RESIDENTIAL && isDefined(podForm.controls.customRange.value)) {
            const zone = NON_RESIDENTIAL_CONSUMPTION_PROFILE_TEMPLATE_ZONES[this.onboardingMember.community_zone];
            profileTemplate = getNonResidentialConsumptionProfileTemplate(
                this.economicsConfig.non_residential_consumption_profile_template,
                pod.pod_utility_type,
                zone,
                +podForm.controls.customRange.value,
            );
        }
        return profileTemplate;
    }

    private validateFile(file: File): boolean {
        if (!FILE_MIME_TYPES.find(mt => mt.type === file.type)) {
            this.msgBoxService.error(this.i18n.translate('File {0} is not a {1} file', file.name, this.mimeTypesNames));
            return false;
        }
        if (file.size > MAX_FILE_MB * 1048576) {
            this.msgBoxService.error(this.i18n.translate('Size of file {0} must be below {1} MB', file.name, MAX_FILE_MB));
            return false;
        }
        return true;
    }

    private validateDocuments(): boolean {
        // check if every mandatory document has been uploaded
        const alreadyUploaded = this.onboardingMember.member.documents
            .map(d => d.document_type);
        return this.subjectDocumentConfig
            .filter(dc => !dc.optional)
            .map(dc => dc.name)
            .reduce((res, key) => res && (!!this.tmpSubjectDocuments[key] || alreadyUploaded.includes(key)), true);
    }

    private fillPodsForm() {
        this.onboardingMember.member.pods.forEach((pod) => {
            const podForm = this.fb.group({
                podCode: new FormControl(pod.pod),
                hasWifi: new FormControl({
                        value: this.isDefined(pod.has_wifi) ? pod.has_wifi : false,
                        disabled: !this.isEditable(ConfigurableField.WIFI, pod.has_wifi)
                    },
                    [Validators.required]),
                podVoltage: new FormControl({
                        value: this.isDefined(pod.resource.pod.voltage) ? pod.resource.pod.voltage : VoltageLevel.LOW,
                        disabled: !this.isEditable(ConfigurableField.VOLTAGE, pod.resource.pod.voltage)
                    },
                    [Validators.required]),
                podPhase: new FormControl({
                        value: this.isDefined(pod.resource.pod.phases) ? pod.resource.pod.phases : Phases.SINGLE,
                        disabled: !this.isEditable(ConfigurableField.PHASES, pod.resource.pod.phases)
                    },
                    [Validators.required]),
            });
            if (this.isEditable(ConfigurableField.ENERGY_PROVIDER, pod.energy_provider)) {
                podForm.addControl('podProvider',
                    new FormControl(pod.energy_provider || null,
                        [this.validateIfMandatory(ConfigurableField.ENERGY_PROVIDER, validateNotBlank), Validators.pattern(PROVIDER_PATTERN)]));
            }
            if (this.isEditable(ConfigurableField.CONTRACTUAL_POWER, pod.resource.pod.contractual_power)) {
                podForm.addControl('contractualPower',
                    new FormControl(pod.resource.pod.contractual_power ?? null,
                        [this.validateIfMandatory(ConfigurableField.CONTRACTUAL_POWER, Validators.required)]));
            }
            if (this.isEditable(ConfigurableField.LAND_REGISTRY_SECTION, pod.land_registry_data?.section)) {
                podForm.addControl('landRegistrySection',
                    new FormControl(pod.land_registry_data?.section || null,
                        [this.validateIfMandatory(ConfigurableField.LAND_REGISTRY_SECTION, validateNotBlank)]));
            }
            if (this.isEditable(ConfigurableField.LAND_REGISTRY_SHEET, pod.land_registry_data?.sheet)) {
                podForm.addControl('landRegistrySheet',
                    new FormControl(pod.land_registry_data?.sheet || null,
                        [this.validateIfMandatory(ConfigurableField.LAND_REGISTRY_SHEET, validateNotBlank)]));
            }
            if (this.isEditable(ConfigurableField.LAND_REGISTRY_PARTICLE, pod.land_registry_data?.particle)) {
                podForm.addControl('landRegistryParticle',
                    new FormControl(pod.land_registry_data?.particle || null,
                        [this.validateIfMandatory(ConfigurableField.LAND_REGISTRY_PARTICLE, validateNotBlank)]));
            }
            if (this.isEditable(ConfigurableField.LAND_REGISTRY_SUBDIVISION, pod.land_registry_data?.subdivision)) {
                podForm.addControl('landRegistrySubdivision',
                    new FormControl(pod.land_registry_data?.subdivision || null,
                        [this.validateIfMandatory(ConfigurableField.LAND_REGISTRY_SUBDIVISION, validateNotBlank)]));
            }
            pod.resource.plants.forEach(plant => {
                if (this.isEditable(ConfigurableField.PRODUCTION_POD, plant.plant.production_pod)) {
                    podForm.addControl(`pod${plant.id}Production`,
                        new FormControl(plant.plant.production_pod || null,
                            [this.validateIfMandatory(ConfigurableField.PRODUCTION_POD, validateNotBlank), Validators.pattern(PRODUCTION_POD_PATTERN)]));
                }
            });
            this.podCtrl.push(podForm);

            if (this.isPodProfilable(pod)) {
                const profileRequiredValidator = this.validateIfMandatory(ConfigurableField.ENERGY_PROFILE, Validators.required);
                const floatValidator = (ctrl: AbstractControl) => validateFloat(ctrl, this.i18n.language);
                const currYearlyConsumption = this.getYearlyConsumption(pod.resource);
                const podProfileForm = this.fb.group(
                    {
                        podCode: new FormControl(pod.pod),
                        podProfileRadio: new FormControl(isDefined(currYearlyConsumption) ? 1 : (this.podIsReligiousInstitution(pod) ? 1 : 0), []),
                        podYearlyConsumption: new FormControl(this.formatFloat(currYearlyConsumption),
                            (this.podIsReligiousInstitution(pod) || this.podIsThirdSector(pod) || this.podIsOther(pod)) ?
                                [profileRequiredValidator, floatValidator] :
                                [floatValidator]),
                        podEstimatedYearlyConsumption: new FormControl(0, [])
                    }
                );
                if (!this.isCompany) {
                    podProfileForm.addControl('podPeople', new FormControl(null, [profileRequiredValidator, validateInteger]));
                    podProfileForm.addControl('podTv', new FormControl(null, [profileRequiredValidator, validateInteger]));
                    podProfileForm.addControl('podPc', new FormControl(null, [profileRequiredValidator, validateInteger]));
                    podProfileForm.addControl('podAc', new FormControl(null, [profileRequiredValidator, validateInteger]));
                    podProfileForm.addControl('podDishwasher', new FormControl(false, [profileRequiredValidator]));
                    podProfileForm.addControl('podHeater', new FormControl(false, [profileRequiredValidator]));
                } else {
                    podProfileForm.addControl('customRange', new FormControl(null,
                        isDefined(currYearlyConsumption) || (this.podIsReligiousInstitution(pod) || this.podIsThirdSector(pod) || this.podIsOther(pod)) ?
                            [] :
                            [profileRequiredValidator]));
                }
                this.podProfileCtrl.push(podProfileForm);
            }
        });
    }

    private formatFloat(val: number): string {
        return isDefined(val) ? Number(val).toString().replace('.', this.i18n.decimalSeparator(DEFAULT_DECIMAL_SEPARATOR)) : null;
    }

    private sanityReplaceFloat(val: string): number {
        return val && val.trim() ? +val.replace(',', '.') : null;
    }

    private openSpinner(message: string) {
        const spinnerData = new BehaviorSubject<SpinnerData>({
            loading: true,
            loadingMessage: this.i18n.translate(message)
        });
        return this.dialogService.open<Subject<SpinnerData>, undefined>({
            template: this.spinnerDialog,
            context: spinnerData
        }, {
            width: DIALOG_WIDTH,
            hideCloseButton: true
        });
    }

    private mkPswErrorMsg() {
        this.pwErrorMessages = [];
        this.messages.forEach(message => {
            let cleanMsg = message.message;
            if (parseInt(message.argument, 10) < 2) {
                if (message.message.includes('digits')) {
                    cleanMsg = message.message.replace('digits', 'digit');
                } else if (message.message.includes('characters')) {
                    cleanMsg = message.message.replace('characters', 'character');
                }
            }
            this.pwErrorMessages.push(this.i18n.translate(('Must ' + cleanMsg), message.argument));
        });
    }

    private measure(pwd: string) {
        this.apiService
            .measurePasswordStrength(pwd)
            .then(s => {
                this.strength.quality = s.quality;
                this.strength.validity = s.valid;
                this.messages = s.messages as PasswordStrengthMessage[];
                this.mkPswErrorMsg();
            })
            .catch(err => {
                console.error(`Cannot measure password strength`, err);
            });
    }

    private pwValidator(): ValidatorFn {
        return (ctrl: AbstractControl): ValidationErrors | null => {
            const errs: ValidationErrors = {};
            const data = this.route.snapshot.data.member.member.personal_data;
            const ctrls: { [ctrl: string]: AbstractControl } = (ctrl as FormGroup).controls;
            if (this.containsForbiddenWords(ctrls.password.value, [data.first_name, data.last_name, data.email])) {
                ctrls.password.setErrors({forbidden_words: true});
            }
            return Object.values(errs).every(err => !err) ? null : errs;
        };
    }

    private countdown() {
        const interval = 1000;
        this.savedTimeToSleep = localStorage.getItem('savedTimeToSleep');
        if (this.savedTimeToSleep === null) {
            this.timeToSleep = TOTP_TIMEOUT;
            localStorage.setItem('savedTimeToSleep', this.timeToSleep.toString());
        } else {
            this.timeToSleep = Number(this.savedTimeToSleep);
        }
        const timer = setInterval(() => {
            this.timeToSleep -= interval;
            const minutes = Math.floor((this.timeToSleep / 1000) / 60);
            const seconds = Math.floor((this.timeToSleep / 1000) % 60);
            this.timerMessage = '0' + minutes + ':' + (seconds < 10 ? '0' + seconds : seconds);
            localStorage.setItem('savedTimeToSleep', this.timeToSleep.toString());
            if (this.timeToSleep < 0) {
                clearInterval(timer);
                localStorage.removeItem('savedTimeToSleep');
                this.timerMessage = '';
                this.timeToSleep = TOTP_TIMEOUT;
            }
        }, interval);
    }


    private containsForbiddenWords(target: string, forbiddenWords: string[]): boolean {
        return target ? [...forbiddenWords, ...environment.forbiddenWords]
            .some(fw => target.toLowerCase().includes(fw?.toLowerCase() || null)) : false;
    }

    private getPodStatisticalProfileRequestPlantType(plant: EnergyCommunityNamedPlant): PodStatisticalProfileRequestPlantType {
        switch (plant.plant.sub_type) {
            case EntitySubType.WIND_PLANT:
                return PodStatisticalProfileRequestPlantType.WIND;
            case EntitySubType.PV_PLANT:
            default:
                return PodStatisticalProfileRequestPlantType.PV;
        }
    }

    private mkBirthMunicipalityValidator(): ValidatorFn {
        const mandatoryValidator = this.validateIfMandatory(ConfigurableField.BIRTH_MUNICIPALITY, Validators.required);
        return ctrl => {
            // municipality is only mandatory if country of birth is Italy
            if (this.bornInItaly) {
                return mandatoryValidator(ctrl);
            }
            return null;
        };
    }

    private validateIfMandatory(field: ConfigurableField, validator: ValidatorFn): ValidatorFn {
        const fieldCfg = this.fieldConfigs.find(cfg => cfg.field === field);
        return fieldCfg?.mandatory ? validator : (() => null);
    }

    private getYearlyConsumption(pod: EnergyCommunityNamedPod): number | null {
        const profile = pod.pod.statistical_profile;
        if (!profile) {
            return null; // no profile at all
        }
        if (this.isConsumer(pod)) {
            // sum energy absorbed within each time slot, regardless of the rate mode
            return sumEnergies(profile.absorbed_monthly_energies);
        } else if (this.isProsumer(pod)) {
            // sum energy absorbed within each time slot (regardless of the rate mode), then subtract injected energy
            // and finally sum the total energy produced by each underlying plant
            const producedEnergy = pod.plants.map(plant => sumEnergies(plant.plant.statistical_profile?.produced_monthly_energies))
                .reduce((e1, e2) => e1 + e2, 0);
            return Math.max(sumEnergies(profile.absorbed_monthly_energies) - sumEnergies(profile.injected_monthly_energies) + producedEnergy, 0);
        }
        return null; // no consumption for producers
    }

    private isPodProfilable(pod: ECMemberPOD): boolean {
        // NOTE: currently, producer PODs are *never* profilable
        return this.isEditable(ConfigurableField.ENERGY_PROFILE, pod.resource.pod.statistical_profile) &&
            !this.isProducer(pod.resource);
    }
}

function samePwdValidator(): ValidatorFn {
    return form => validateEq((form as FormGroup).controls.password, (form as FormGroup).controls.repeatedPassword);
}


function getContractualPowerValues(): EnumValue[] {
    return [...Array(201).keys()].map(x => x / 2).slice(1) // numbers from 0.5 to 100 with 0.5 increments
        .map(value => ({value: value * 1000, name: value.toString(10) + ' kW'}));
}

function mkLandRegistryData(pod: ECMemberPOD, podForm: FormGroup): ECLandRegistryData | null {
    const section = podForm.controls.landRegistrySection ? mkTextField(podForm.controls.landRegistrySection.value) : pod.land_registry_data?.section || null;
    const sheet = podForm.controls.landRegistrySheet ? mkTextField(podForm.controls.landRegistrySheet.value) : pod.land_registry_data?.sheet || null;
    const particle = podForm.controls.landRegistryParticle ? mkTextField(podForm.controls.landRegistryParticle.value) : pod.land_registry_data?.particle || null;
    const subdivision = podForm.controls.landRegistrySubdivision ? mkTextField(podForm.controls.landRegistrySubdivision.value) : pod.land_registry_data?.subdivision || null;
    return !!section || !!sheet || !!particle || !!subdivision ? {
        section,
        sheet,
        particle,
        subdivision
    } : null;
}

function sumEnergies(energies: { [slot in TimeSlot]?: number[] }): number {
    if (!energies) {
        return 0;
    }
    let total = 0;
    Object.keys(energies).forEach((slot: TimeSlot) => {
        total += energies[slot as TimeSlot].reduce((e1, e2) => e1 + e2, 0);
    });
    return total;
}

function isFieldValueDefined(value: any): boolean {
    // empty strings count as "undefined" for this purpose
    if (typeof value === 'string') {
        return !!value.length;
    }
    return isDefined(value);
}
